/**
 * Shared DNP (Did Not Play) utilities for tracked parlays.
 *
 * Both web and mobile need to combine two DNP sources:
 * 1. `leg.dnp` — settled by the server (DB field)
 * 2. `isPlayerDNP()` — live detection from box score data
 *
 * This module provides shared helpers so the combining logic isn't duplicated.
 */

import { americanToDecimal, decimalToAmerican } from './oddsUtils';

/** Callback signature for live DNP detection (same on web and mobile) */
export type IsPlayerDNPFn = (
  gameId: string | null,
  playerId: number | null,
  playerName: string | null,
) => boolean | null;

const GAME_LEVEL_STAT_TYPES = new Set(['h2h', 'spreads', 'totals', 'spread', 'total', 'moneyline']);

/** Minimal leg shape needed for DNP checks */
interface DnpCheckLeg {
  dnp?: boolean | null;
  settled?: boolean;
  player?: string | null;
  playerId?: number | null;
  statType?: string | null;
  gameId?: string | null;
}

/**
 * Check if a leg is effectively DNP — either settled DNP or live-detected DNP.
 * Use this everywhere instead of checking `leg.dnp` directly.
 */
export function isLegEffectivelyDnp(leg: DnpCheckLeg, isPlayerDNP?: IsPlayerDNPFn): boolean {
  if (leg.dnp) return true;
  if (!isPlayerDNP || leg.settled) return false;
  if (!leg.player) return false;
  // Game-level props (spread, total, moneyline) don't have player DNP
  const st = (leg.statType ?? '').toLowerCase();
  if (GAME_LEVEL_STAT_TYPES.has(st)) return false;
  return (
    isPlayerDNP(
      leg.gameId ?? null,
      typeof leg.playerId === 'number' ? leg.playerId : null,
      leg.player,
    ) === true
  );
}

/**
 * Count legs that are effectively DNP in a list.
 */
export function countEffectiveDnpLegs(legs: DnpCheckLeg[], isPlayerDNP?: IsPlayerDNPFn): number {
  return legs.filter((leg) => isLegEffectivelyDnp(leg, isPlayerDNP)).length;
}

/**
 * Minimal parlay shape for DNP-adjusted odds. Leg odds are American strings on both
 * platforms; parlay-level odds are strings on web and numbers on mobile.
 */
export interface DnpAdjustableParlay {
  finalOdds?: string | number | null;
  totalOdds?: string | number | null;
  legs: Array<DnpCheckLeg & { odds?: string | null }>;
}

/**
 * DNP-adjusted parlay odds, for display and for the potential-payout figure.
 * - Settled: settlement already recalculated the odds after voids, so return finalOdds.
 * - Pending: drop live-detected and settled DNP legs and recompute from the rest, since
 *   the book voids a DNP leg and reprices the parlay at lower odds.
 * Returns null when no adjustment applies (no DNP legs, or every leg DNP) so the caller
 * falls back to the original totalOdds. Always returns an American odds STRING: web uses
 * it directly, mobile parses it with Number(). finalOdds/totalOdds are compared with a
 * direct `!==`, which is a string compare on web and a numeric compare on mobile (both
 * fields share a type within a platform), matching each platform's prior behavior.
 */
export function calculateDnpAdjustedOdds(
  parlay: DnpAdjustableParlay,
  isPlayerDNP?: IsPlayerDNPFn,
): string | null {
  if (parlay.finalOdds && parlay.finalOdds !== parlay.totalOdds) {
    return String(parlay.finalOdds);
  }

  const activeLegs = parlay.legs.filter((leg) => !isLegEffectivelyDnp(leg, isPlayerDNP));
  if (activeLegs.length === parlay.legs.length) return null;
  if (activeLegs.length === 0) return null;

  let combinedDecimal = 1;
  for (const leg of activeLegs) {
    if (leg.odds) combinedDecimal *= americanToDecimal(leg.odds);
  }
  return decimalToAmerican(combinedDecimal);
}
